<template>
<Layout>
  <div :class="['sign-up-container', { 'screen-lg': this.el.is.tabletUp }]">
    <div class="brand-wrapper" v-if="this.el.is.tabletUp">
      <div class="logo">
        <img :src="clientLogo" />
      </div>
      <h2 class="brand-text">{{ this.quote }}</h2>
    </div>

    <div class="sign-up-wrapper">
      <div class="logo" v-if="!this.el.is.tabletUp">
        <img :src="clientLogo" />
      </div>
      <form class="form" @submit.prevent="handleSignUp">
        <h1>Sign Up</h1>

        <BaseInput
          :class="{ 'email-error': this.emailError, 'signup--input': true }"
          type="username"
          name="username"
          v-model="username"
          placeholder="E-mail Address"
        />
        <p class="error-msg" v-if="this.emailError">{{ this.emailError }}</p>
        <BaseInput
          :class="{ 'pass-error': this.passError, 'signup--input': true }"
          type="password"
          name="password"
          v-model="password"
          placeholder="Password"
        />
        <p class="error-msg" v-if="this.passError">{{ this.passError }}</p>

        <BaseButton :disabled="authenticating" type="submit">
          <BaseIcon v-if="authenticating" name="sync" spin />
          <span v-else>Sign Up</span>
        </BaseButton>

        <div class="sign-in" v-if="!this.el.is.tabletUp">
          <p>Already have an account?</p>
          <router-link :to="{ name: 'login' }" class="onboarding-link">Sign In</router-link>
        </div>
      </form>

      <div class="sign-in" v-if="this.el.is.tabletUp">
        <p>Already have an account?</p>
        <router-link :to="{ name: 'login' }" class="onboarding-link">Sign In</router-link>
      </div>
    </div>
  </div>
</Layout>
</template>

<script>
import Layout from 'layouts/main';
import appConfig from '@src/app.config.ES6';
import { breakpointsMixin } from 'breakpointsMixin';
import { gtmMixin } from 'gtmMixin';
import { authApiMethods, mainMethods, authComputed, onboardingMethods, onboardingComputed, errorsMethods } from '@state/helpers';

export default {
  page: {
    title: 'Sign Up',
    meta: [{ name: 'description', content: `Sign up to ${appConfig.title}` }],
  },
  props: {
    route: {
      type: Object,
    },
  },
  mixins: [breakpointsMixin, gtmMixin],
  components: { Layout },
  data() {
    return {
      username: '',
      password: '',
      emailError: null,
      passError: null,
      authenticating: false,
    };
  },
  computed: {
    ...onboardingComputed,
    clientName() {
      return appConfig.NAME ? appConfig.NAME : null;
    },
    clientLogo() {
      return appConfig.LOGO;
    },
  },
  mounted() {
    this.gtmTrackEvent('pageView', { action: 'Visit Signup', label: 'Signup View' });
    this.setQuote();
  },
  methods: {
    ...authApiMethods,
    ...errorsMethods,
    ...mainMethods,
    ...onboardingMethods,
    setQuote() {
      const quotes = [
        `"Knowing is not enough; we must apply. Willing is not enough; we must do." –Johann Wolfgang von Goethe`,
        `"I have been impressed with the urgency of doing. Knowing is not enough; we must apply. Being willing is not enough; we must do." –Leonardo da Vinci`,
        `"The results you achieve will be in direct proportion to the effort you apply." –Denis Waitley`,
      ];
      this.quote = quotes[Math.floor(Math.random() * quotes.length)];
    },
    handleAuthErrors(err) {
      this.authenticating = false;

      if (err.data) {
        switch (err.data.Error) {
          case 'username already exists':
            this.emailError = 'Username already exists';
            break;
        }
      } else {
        this.setServerError(err);
      }
    },
    formValid() {
      if (this.username && this.password) {
        return true;
      } else {
        if (!this.password || this.password === '') {
          this.passError = `Password can't be blank`;
        } else if (this.password.length < 8) {
          this.passError = `Password must be at least 8 characters`;
        }

        if (!this.username || this.username === '') {
          this.emailError = `E-mail can't be blank`;
        }
        return false;
      }
    },
    handleSignUp() {
      if (this.formValid()) {
        this.authenticating = true;

        return this.signUp({
          username: this.username,
          email: this.username,
          password: this.password,
        })
          .then((token) => {
            this.authenticating = false;
            if (this.isOnboardingEnabled) {
              this.setIsOnboarding(true);
              this.$router.push({ name: 'onboarding' });
            } else {
              this.$router.push({ name: 'embed' });
            }
          })
          .catch((err) => this.handleAuthErrors(err));
      }
    },
  },
  watch: {
    username(val) {
      if (this.emailError) {
        this.emailError = null;
      }
    },
    password(val) {
      if (this.passError) {
        this.passError = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'theme.scss';

.onboarding-link {
  color: $color-button-text;
  text-decoration: underline;
}

.sign-up-container {
  align-items: center;
  background-color: $color-action;
  bottom: 0;
  display: flex;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;

  .brand-wrapper {
    align-content: center;
    align-items: center;
    background-color: $color-action;
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 100vh;
    justify-content: center;
    padding-top: 8rem;
    text-align: center;
    top: 0;

    .logo {
      display: flex;
      justify-content: center;
      position: absolute;
      top: 10rem;
      width: 100%;

      img {
        height: auto;
        max-height: 15rem;
        width: auto;
      }
    }

    .brand-text {
      font-family: Georgia, 'Times New Roman', Times, serif;
      font-size: 5rem;
      font-style: italic;
      font-weight: 400;
      line-height: 1.1;
      padding: 0 4rem;
    }
  }

  .sign-up-wrapper {
    flex: 1;

    .logo {
      display: flex;
      justify-content: center;
      position: absolute;
      top: 3.5rem;
      width: 100%;

      img {
        height: auto;
        max-height: 15rem;
        width: auto;
      }
    }

    .form {
      margin: 4rem auto 2rem;
      padding: 0 2rem;
      text-align: center;
      width: 100%;

      .reset-password-text {
        font-size: 1.4rem;
      }

      h1 {
        color: $color-button-text;
        font-size: 4rem;
        margin-bottom: 3.2rem;

        // iPad up
        @include media('xl', null) {
          font-size: 4.2rem;
        }
      }

      input {
        line-height: 2;
        margin: 0 auto 1rem auto;
        max-width: 37.5rem;

        &::placeholder {
          color: $color-text;
          line-height: 2;
          opacity: 0.5;
        }

        &.email-error,
        &.pass-error {
          border-color: $color-error;
          border-width: 0.2rem;
          margin-bottom: 1rem;
        }
      }

      .error-msg {
        color: $color-error;
        font-size: 1.4rem;
        font-weight: 400;
        margin: 0 auto 2rem auto;
        max-width: 37.5rem;
        text-align: left;
      }

      .button {
        background-color: $color-button-text;
        color: $color-action;
        padding: 1rem 2rem;

        @include media('xl', null) {
          background-color: $color-action;
          color: $color-button-text;
        }
      }
    }
  }

  .sign-in {
    align-items: center;
    display: flex;
    flex-direction: column;
    font-size: 1.6rem;
    margin-top: 2rem;
  }

  &.screen-lg {
    background-color: $color-body-bg;

    .form {
      .heading {
        font-size: calc(1.476vw + 0.631rem);
        text-align: left;
      }

      .subheading {
        font-size: 1.4rem;
        line-height: 1.2;
        margin-bottom: 2rem;
        text-align: left;
      }
    }

    .sign-in {
      bottom: 0;
      position: relative;
    }
  }
}
</style>
